import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { complete, homeWith, run, suggest } from "../utils/shell";
import { loadTree, loadedTree, openInZed, readFile } from "../utils/project";

// "Last login" is when this browser last opened a terminal here (Ghostty's or Zed's), as the Mac
// keeps one for every terminal.
const LOGIN_KEY = "onlysly:ghostty-login";
const lastLogin = () => {
  try {
    const t = Number(localStorage.getItem(LOGIN_KEY));
    return t ? new Date(t) : null;
  } catch (e) {
    return null;
  }
};
const saveLogin = (date) => {
  try {
    localStorage.setItem(LOGIN_KEY, String(date.getTime()));
  } catch (e) {
    // Private windows may not store anything; the next login just shows this one's time.
  }
};

// How many characters fit across the terminal, for ls to lay out its columns.
const columnsOf = (screen, content) => {
  if (!screen || !content) return 80;
  const probe = document.createElement("span");
  probe.textContent = "0".repeat(20);
  probe.style.cssText = "position:absolute;visibility:hidden;white-space:pre";
  content.appendChild(probe);
  const cell = probe.getBoundingClientRect().width / 20;
  probe.remove();
  const style = getComputedStyle(screen);
  const width = screen.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
  return cell > 0 ? Math.max(20, Math.floor(width / cell)) : 80;
};

let lastId = 0;
// A shell session: what's on screen (`blocks`: the login line, then each command with the prompt
// it was typed at and its output), the working folder, the last command's status, and history.
const newSession = (cwd) => {
  const now = new Date();
  const login = lastLogin() || now;
  saveLogin(now);
  return { blocks: [{ id: ++lastId, login }], cwd, status: 0, history: [], hinted: false };
};

// `open -a`: the apps that open on this Mac, and the events that open them.
const APPS = {
  notes: "open-resume",
  finder: "open-finder",
  "google chrome": "open-chrome",
  chrome: "open-chrome",
  ghostty: "open-ghostty",
  orbstack: "open-orbstack",
  spotify: "open-spotify",
  zed: "open-zed",
};

const actions = {
  openResume: (anchor) => window.dispatchEvent(new CustomEvent("open-resume", { detail: { anchor } })),
  openUrl: (href) => window.open(href, "_blank", "noopener"),
  openApp: (name) => {
    const event = APPS[name.toLowerCase().replace(/\.app$/, "")];
    if (!event) return false;
    window.dispatchEvent(new CustomEvent(event, { detail: {} }));
    return true;
  },
  openFile: openInZed,
  readFile,
};

// A terminal's shell (utils/shell.js), for a window to show: the session, the line being typed
// (`input`, with the caret and the gray suggestion after it), Tab's choices, history, and keeping
// the newest line in view. `shell` is "zsh" or "bash", `start` the folder it starts in. While
// `active`, the terminal has the keyboard, and typing anywhere goes to it unless another text field
// has it. `onExit` is for `exit`.
const HOME_CWD = [];

const useShell = ({ open, active, shell = "zsh", start = HOME_CWD, onExit }) => {
  const [session, setSession] = useState(null);
  const [home, setHome] = useState(() => homeWith(loadedTree()));
  const [input, setInput] = useState("");
  const [caret, setCaret] = useState(0);
  const [focused, setFocused] = useState(false);
  const [options, setOptions] = useState([]); // Tab's choices, shown until the next key
  const [recall, setRecall] = useState({ pos: null, draft: "" }); // stepping through history
  const [screen, setScreen] = useState(null);
  const [content, setContent] = useState(null);
  const field = useRef(null);
  // Whether the newest line is in view, and where scrolling to it last left the screen.
  const atBottom = useRef(true);
  const pinnedAt = useRef(null);
  const moveCaret = useRef(null);

  // A session starts the first time the terminal opens, and again after it's reset. The project
  // joins the home folder once its tree has loaded.
  useLayoutEffect(() => {
    if (open && !session) setSession(newSession(start));
  }, [open, session, start]);

  useEffect(() => {
    if (open)
      loadTree().then(
        (tree) => setHome(homeWith(tree)),
        () => {},
      );
  }, [open]);

  useEffect(() => {
    const el = field.current;
    if (!el) return;
    if (active) el.focus({ preventScroll: true });
    else el.blur();
  }, [active, session]);

  // Typing while it's active goes to the terminal, unless another text field has the keyboard
  // (the Dock's buttons, say, keep it after a click; macOS's never take it).
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      const el = document.activeElement;
      const typing = el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
      if (field.current && !typing) field.current.focus({ preventScroll: true });
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [active]);

  // Keep the newest line in view, as a terminal does when you type or a command prints, and when
  // lines rewrap (the window resizes, or its font arrives) unless you've scrolled back.
  const toBottom = () => {
    if (!screen) return;
    screen.scrollTop = screen.scrollHeight;
    pinnedAt.current = screen.scrollTop;
  };

  useLayoutEffect(() => {
    atBottom.current = true;
    toBottom();
  }, [session, input, options, screen, content]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only a scroll you make says whether you've left the bottom. The scroll from toBottom() can
  // arrive after the lines have rewrapped again, when it no longer reads as the bottom.
  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop === pinnedAt.current) return;
    atBottom.current = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
  };

  useEffect(() => {
    if (!screen || !content) return undefined;
    const observer = new ResizeObserver(() => {
      if (!atBottom.current) return;
      screen.scrollTop = screen.scrollHeight;
      pinnedAt.current = screen.scrollTop;
    });
    observer.observe(screen);
    observer.observe(content);
    return () => observer.disconnect();
  }, [screen, content]);

  // After the line changes from code (history, Tab), put the caret where it belongs.
  useLayoutEffect(() => {
    if (moveCaret.current === null || !field.current) return;
    field.current.setSelectionRange(moveCaret.current, moveCaret.current);
    moveCaret.current = null;
  });

  const setLine = (text, at = text.length) => {
    setInput(text);
    setCaret(at);
    moveCaret.current = at;
    setOptions([]);
  };

  // Ends the session: the next open starts a new one.
  const reset = () => {
    setSession(null);
    setLine("");
    setRecall({ pos: null, draft: "" });
  };

  // Ends the line being typed: `text` is what stays after the prompt, `result` what it printed,
  // and `command` what goes into history. Output still on its way (a project file for cat) fills
  // in its block when it arrives.
  const finish = (text, result, command = "") => {
    const id = ++lastId;
    setSession((s) => ({
      ...s,
      blocks: result.clear ? [] : [...s.blocks, { id, cwd: s.cwd, status: s.status, text, lines: result.lines }],
      cwd: result.cwd || s.cwd,
      status: result.status === undefined ? s.status : result.status,
      history: command && command !== s.history[s.history.length - 1] ? [...s.history, command] : s.history,
      hinted: s.hinted || Boolean(result.hinted),
    }));
    if (result.pending)
      result.pending.then((later) =>
        setSession((s) =>
          s && s.blocks.some((b) => b.id === id)
            ? {
                ...s,
                blocks: s.blocks.map((b) => (b.id === id ? { ...b, lines: later.lines } : b)),
                status: later.status,
              }
            : s,
        ),
      );
    setLine("");
    setRecall({ pos: null, draft: "" });
  };

  const submit = () => {
    const result = input.trim()
      ? run(input, {
          shell,
          home,
          columns: columnsOf(screen, content),
          cwd: session.cwd,
          status: session.status,
          history: [...session.history, input.trim()],
          hinted: session.hinted,
          actions,
        })
      : { lines: [] };
    finish(input, result, input.trim());
    if (result.exit) onExit();
  };

  const step = (delta) => {
    const { history } = session;
    if (!history.length) return;
    const from = recall.pos === null ? history.length : recall.pos;
    const pos = Math.min(history.length, Math.max(0, from + delta));
    const draft = recall.pos === null ? input : recall.draft;
    setRecall({ pos: pos === history.length ? null : pos, draft });
    setLine(pos === history.length ? draft : history[pos]);
  };

  // Gray text after the cursor: a suggestion as you type, and "help" until you've run something.
  const suggestion = () => {
    if (!session || caret !== input.length) return "";
    if (input) return suggest(input, session.history);
    return session.history.length ? "" : "help";
  };
  const ghost = suggestion();

  const onKeyDown = (e) => {
    const key = e.key;
    if (key === "Enter") {
      e.preventDefault();
      submit();
    } else if (key === "Tab") {
      e.preventDefault();
      const done = complete(input.slice(0, caret), session.cwd, home);
      setLine(done.input + input.slice(caret), done.input.length);
      setOptions(done.options);
    } else if (key === "ArrowUp" || key === "ArrowDown") {
      e.preventDefault();
      step(key === "ArrowUp" ? -1 : 1);
    } else if ((key === "ArrowRight" || key === "End") && ghost && !e.shiftKey) {
      e.preventDefault();
      setLine(input + ghost);
    } else if (e.ctrlKey && key === "c") {
      e.preventDefault();
      finish(`${input}^C`, { lines: [] });
    } else if ((e.ctrlKey && key === "l") || (e.metaKey && key === "k")) {
      // Clear the screen (⌘K in Ghostty and Zed), rather than open Spotlight.
      e.preventDefault();
      e.stopPropagation();
      setSession((s) => ({ ...s, blocks: [] }));
    } else if (e.ctrlKey && key === "u") {
      e.preventDefault();
      setLine(input.slice(caret), 0);
    }
  };

  // A click puts the keyboard in the terminal, unless it was to select text.
  const onMouseUp = () => {
    const selection = window.getSelection();
    if ((!selection || selection.isCollapsed) && field.current) field.current.focus({ preventScroll: true });
  };

  return {
    session,
    input,
    caret,
    ghost,
    options,
    focused,
    reset,
    screenRef: setScreen,
    contentRef: setContent,
    onScroll,
    onMouseUp,
    fieldProps: {
      ref: field,
      value: input,
      onChange: (e) => {
        setInput(e.target.value);
        setCaret(e.target.selectionStart);
        setOptions([]);
      },
      onSelect: (e) => setCaret(e.target.selectionStart),
      onKeyDown,
      onFocus: () => setFocused(true),
      onBlur: () => setFocused(false),
      "aria-label": "Terminal",
      autoComplete: "off",
      autoCorrect: "off",
      autoCapitalize: "off",
      spellCheck: false,
    },
  };
};

export default useShell;
